import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { LoginContext } from '../../reactcontext/ReactContext';
import Modal from '../../components/Modal';
import { useNavigate } from 'react-router-dom';
import { years } from '../../data/options';

const RecordPayment = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { loginInfo } = useContext(LoginContext);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [agentOptions, setAgentOptions] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [nameOptions, setNameOptions] = useState([]);
  const [selectedName, setSelectedName] = useState(null);
  const [passportOptions, setPassportOptions] = useState([]);
  const [selectedPassport, setSelectedPassport] = useState(null);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [balance, setBalance] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [paymentMode, setPaymentMode] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [otherDetails, setOtherDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);

  const paymentModes = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Card', label: 'Card' },
    { value: 'Online', label: 'Online' },
  ];

  useEffect(() => {
    if (!loginInfo.login) return;
    fetchEmployees();
  }, [loginInfo]);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${apiUrl}/employee/`, {
        headers: { loginToken: localStorage.getItem('loginToken') },
      });
      setEmployees(res.data);
      setFilteredEmployees(res.data);
      // Build agent options
      const agents = Array.from(new Set(res.data.map(emp => emp.Agent?.name || 'Unknown')));
      setAgentOptions(agents.map(a => ({ value: a, label: a })));
      // Build name options
      const names = Array.from(new Set(res.data.map(emp => `${emp.firstName} ${emp.lastName}`)));
      setNameOptions(names.map(n => ({ value: n, label: n })));
      // Build passport options
      const passports = Array.from(new Set(res.data.map(emp => emp.Passports?.[0]?.passportNo).filter(Boolean)));
      setPassportOptions(passports.map(p => ({ value: p, label: p })));
    } catch (err) {
      console.error(err);
    }
  };

  // Filtering logic
  useEffect(() => {
    let filtered = employees;
    if (selectedName) {
      filtered = filtered.filter(emp => `${emp.firstName} ${emp.lastName}` === selectedName.value);
    }
    if (selectedPassport) {
      filtered = filtered.filter(emp => emp.Passports?.[0]?.passportNo === selectedPassport.value);
    }
    if (selectedAgent) {
      filtered = filtered.filter(emp => emp.Agent?.name === selectedAgent.value);
    }
    setFilteredEmployees(filtered);
  }, [selectedName, selectedPassport, selectedAgent, employees]);

  const fetchYearlyPaymentData = async (employeeId, year, totalAmount) => {
    try {
      const res = await axios.get(`${apiUrl}/payment/getPaymentsWithBalance/${employeeId}/${year}`, {
        headers: { loginToken: localStorage.getItem('loginToken') },
      });
      if (res.data.error) {
        setTotalPaid(0);
        setBalance(totalAmount);
      } else {
        setTotalPaid(res.data.totalPayments);
        setBalance(res.data.balance);
      }
    } catch (err) {
      setTotalPaid(0);
      setBalance(totalAmount);
    }
  };

  const handleEmployeeChange = async (selected) => {
    setSelectedEmployee(selected);
    if (selected) {
      setEmployeeDetails(selected.data);
      const total = parseFloat(selected.data.visaFee || 0) + parseFloat(selected.data.epfFee || 0) + parseFloat(selected.data.socsoFee || 0) + parseFloat(selected.data.insuranceFee || 0);
      setTotalAmount(total);
      await fetchYearlyPaymentData(selected.value, selectedYear, total);
    } else {
      setEmployeeDetails(null);
      setTotalAmount(0);
      setTotalPaid(0);
      setBalance(0);
    }
  };

  const handlePaymentAmountChange = (e) => {
    const amt = e.target.value;
    setPaymentAmount(amt);
    const remaining = balance - parseFloat(amt || 0);
    // Balance updates live
  };

  const handleYearChange = async (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    if (selectedEmployee) {
      const total = parseFloat(selectedEmployee.data.visaFee || 0) + parseFloat(selectedEmployee.data.epfFee || 0) + parseFloat(selectedEmployee.data.socsoFee || 0) + parseFloat(selectedEmployee.data.insuranceFee || 0);
      await fetchYearlyPaymentData(selectedEmployee.value, year, total);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmployee || !paymentAmount || !paymentMode) {
      setError('Please fill all required fields');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const totalAmount =
        parseFloat(employeeDetails?.visaFee || 0) +
        parseFloat(employeeDetails?.epfFee || 0) +
        parseFloat(employeeDetails?.socsoFee || 0) +
        parseFloat(employeeDetails?.insuranceFee || 0);

      const res = await axios.post(`${apiUrl}/payment/recordPayment`, {
        employeeId: selectedEmployee.value,
        year: selectedYear,
        amount: paymentAmount,
        totalAmount,
        dateOfPayment: paymentDate.toISOString().split('T')[0],
        paymentMode,
        referenceNumber,
        otherDetails,
      }, {
        headers: { loginToken: localStorage.getItem('loginToken') },
      });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setShowRecordForm(false);
        navigate('/payments');
      }, 1500);
      setPaymentAmount('');
      setReferenceNumber('');
      setOtherDetails('');
      handleEmployeeChange(selectedEmployee);
    } catch (err) {
      setError(err.response?.data?.error || 'Error recording payment');
    }
    setLoading(false);
  };

  return (
    <div className='w-full bg-gray-50 flex h-full flex-col items-center justify-between relative gap-3'>
      <div className='self-start ml-10 mt-10'>
        <Link to={'/payments'} className='text-yellow-700'>Payments</Link> {' > '} Record Payment
      </div>
      <div className='w-full p-4 flex flex-col items-center justify-center gap-4'>
        <h1 className='text-2xl font-bold mb-6 text-center'>Record Payment</h1>
        {/* Filter Section - Full Width & Professional */}
        <div className='w-full flex flex-col items-center'>
          <div className='w-full max-w-full bg-white shadow-lg rounded-xl p-8 flex flex-wrap justify-center gap-8 mb-10 border border-gray-200'>
            <Select
              options={nameOptions}
              value={selectedName}
              onChange={setSelectedName}
              placeholder='Search by name'
              isClearable
              isSearchable
              className='w-72 min-w-[220px]'
              styles={{
                control: (base) => ({ ...base, minHeight: 48, fontSize: 16 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            <Select
              options={passportOptions}
              value={selectedPassport}
              onChange={setSelectedPassport}
              placeholder='Search by passport number'
              isClearable
              isSearchable
              className='w-72 min-w-[220px]'
              styles={{
                control: (base) => ({ ...base, minHeight: 48, fontSize: 16 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            <Select
              options={agentOptions}
              value={selectedAgent}
              onChange={setSelectedAgent}
              placeholder='Filter by agent'
              isClearable
              isSearchable
              className='w-72 min-w-[220px]'
              styles={{
                control: (base) => ({ ...base, minHeight: 48, fontSize: 16 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
        </div>
        {/* Employee List */}
        <div className='w-full px-4'>
          <table className='w-full bg-white shadow rounded'>
            <thead>
              <tr className='bg-gray-200'>
                <th className='p-2'>Name</th>
                <th className='p-2'>Passport</th>
                <th className='p-2'>Nationality</th>
                <th className='p-2'>Agent</th>
                <th className='p-2'>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map(emp => (
                <tr key={emp.id} className='border-t'>
                  <td className='p-2'>{emp.firstName} {emp.lastName}</td>
                  <td className='p-2'>{emp.Passports?.[0]?.passportNo || 'N/A'}</td>
                  <td className='p-2'>{emp.nationality}</td>
                  <td className='p-2'>{emp.Agent?.name || 'Unknown'}</td>
                  <td className='p-2'>
                    <button
                      className='bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600'
                      onClick={() => {
                        setSelectedEmployee({ value: emp.id, label: `${emp.firstName} ${emp.lastName} (${emp.Passports?.[0]?.passportNo || 'N/A'})`, data: emp });
                        setEmployeeDetails(emp);
                        setShowRecordForm(true);
                      }}
                    >
                      Record
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Record Form Modal */}
        <Modal
          isOpen={showRecordForm}
          onClose={() => setShowRecordForm(false)}
          dimensions="w-11/12 max-w-4xl h-auto max-h-[80vh] overflow-y-auto"
        >
          <div className='w-full bg-white rounded-lg shadow-lg'>
            {/* Title Only */}
            <div className='bg-gradient-to-r from-blue-50 to-blue-100 rounded-t-lg px-8 pt-6 pb-2 border-b-2 border-gray-200 shadow-sm'>
              <h3 className='text-3xl font-extrabold text-blue-800 tracking-wide text-center mb-1'>Record Payment</h3>
              <p className='text-sm text-blue-600 text-center mt-2 font-medium'>Note: Only one payment record is allowed per employee per year</p>
            </div>
            {/* Employee Details - Full Width, No Padding, No Margin */}
            <div className='w-full bg-white'>
              <div className='px-0 py-4 bg-white rounded-none shadow-none border-b border-blue-100 w-full text-center'>
                <h3 className='text-xl font-bold text-blue-700 mb-3 tracking-wide'>Employee Details</h3>
                <div className='space-y-2 text-base flex flex-col items-center'>
                  <div><span className='font-semibold text-gray-700'>Name:</span> <span className='text-gray-900'>{employeeDetails?.firstName} {employeeDetails?.lastName}</span></div>
                  <div><span className='font-semibold text-gray-700'>Passport:</span> <span className='text-gray-900'>{employeeDetails?.Passports?.[0]?.passportNo || 'N/A'}</span></div>
                  <div><span className='font-semibold text-gray-700'>Nationality:</span> <span className='text-gray-900'>{employeeDetails?.nationality}</span></div>
                  <div><span className='font-semibold text-gray-700'>Agent:</span> <span className='text-gray-900'>{employeeDetails?.Agent?.name}</span></div>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit} className='p-4'>
              {/* Fees Section */}
              <div className='mb-6'>
                <h4 className='text-lg font-semibold mb-3 border-b pb-1'>Fee Breakdown</h4>
                <table className='w-full border-collapse border border-gray-300'>
                  <thead>
                    <tr className='bg-gray-100'>
                      <th className='border border-gray-300 p-2 text-left'>Fee Type</th>
                      <th className='border border-gray-300 p-2 text-right'>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='border border-gray-300 p-2'>Visa Fee</td>
                      <td className='border border-gray-300 p-2 text-right'>
                        <input
                          type='number'
                          step='0.01'
                          value={employeeDetails?.visaFee || ''}
                          onChange={e => setEmployeeDetails({ ...employeeDetails, visaFee: e.target.value })}
                          className='w-full text-right border-none bg-transparent focus:outline-none'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='border border-gray-300 p-2'>EPF Fee</td>
                      <td className='border border-gray-300 p-2 text-right'>
                        <input
                          type='number'
                          step='0.01'
                          value={employeeDetails?.epfFee || ''}
                          onChange={e => setEmployeeDetails({ ...employeeDetails, epfFee: e.target.value })}
                          className='w-full text-right border-none bg-transparent focus:outline-none'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='border border-gray-300 p-2'>SOCSO Fee</td>
                      <td className='border border-gray-300 p-2 text-right'>
                        <input
                          type='number'
                          step='0.01'
                          value={employeeDetails?.socsoFee || ''}
                          onChange={e => setEmployeeDetails({ ...employeeDetails, socsoFee: e.target.value })}
                          className='w-full text-right border-none bg-transparent focus:outline-none'
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className='border border-gray-300 p-2'>Insurance Fee</td>
                      <td className='border border-gray-300 p-2 text-right'>
                        <input
                          type='number'
                          step='0.01'
                          value={employeeDetails?.insuranceFee || ''}
                          onChange={e => setEmployeeDetails({ ...employeeDetails, insuranceFee: e.target.value })}
                          className='w-full text-right border-none bg-transparent focus:outline-none'
                        />
                      </td>
                    </tr>
                    <tr className='bg-gray-100 font-semibold'>
                      <td className='border border-gray-300 p-2'>Total Amount</td>
                      <td className='border border-gray-300 p-2 text-right'>
                        {(
                          parseFloat(employeeDetails?.visaFee || 0) +
                          parseFloat(employeeDetails?.epfFee || 0) +
                          parseFloat(employeeDetails?.socsoFee || 0) +
                          parseFloat(employeeDetails?.insuranceFee || 0)
                        ).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Payment Details */}
              <div className='mb-6'>
                <h4 className='text-lg font-semibold mb-3 border-b pb-1'>Payment Details</h4>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Payment Amount</label>
                    <input
                      type='number'
                      step='0.01'
                      value={paymentAmount}
                      onChange={handlePaymentAmountChange}
                      className='w-full p-2 border rounded focus:ring-2 focus:ring-blue-500'
                      required
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Year</label>
                    <select
                      value={selectedYear}
                      onChange={handleYearChange}
                      className='w-full p-2 border rounded focus:ring-2 focus:ring-blue-500'
                    >
                      {years.map(year => (
                        <option key={year} value={String(year)}>{year}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Payment Date</label>
                    <DatePicker
                      selected={paymentDate}
                      onChange={setPaymentDate}
                      className='w-full p-2 border rounded focus:ring-2 focus:ring-blue-500'
                      dateFormat='yyyy-MM-dd'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Payment Mode</label>
                    <Select
                      options={paymentModes}
                      value={paymentModes.find(m => m.value === paymentMode)}
                      onChange={(selected) => setPaymentMode(selected.value)}
                      placeholder="Select payment mode"
                      className='w-full'
                      styles={{
                        control: (base) => ({ ...base, padding: '4px', borderRadius: '4px' }),
                      }}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium mb-1'>Reference Number (Optional)</label>
                    <input
                      type='text'
                      value={referenceNumber}
                      onChange={(e) => setReferenceNumber(e.target.value)}
                      className='w-full p-2 border rounded focus:ring-2 focus:ring-blue-500'
                    />
                  </div>
                </div>
              </div>
              {error && <p className='text-red-500 mb-4 text-center'>{error}</p>}
              <div className='flex justify-end gap-4 border-t pt-4'>
                <button
                  type='button'
                  onClick={() => setShowRecordForm(false)}
                  className='px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={loading}
                  className='px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50'
                >
                  {loading ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
      {showSuccess && (
        <div className='fixed inset-0 flex items-center justify-center z-50'>
          <div className='bg-white border border-green-300 shadow-lg rounded-xl px-8 py-6 flex flex-col items-center'>
            <svg className='w-12 h-12 text-green-500 mb-2' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
            </svg>
            <div className='text-lg font-semibold text-green-700 mb-1'>Saved successfully!</div>
            <div className='text-gray-500'>Redirecting to General Payments...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordPayment;